import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { LoginPage } from "@/pages/LoginPage";
import { InviteRedeemPage } from "@/pages/InviteRedeemPage";
import { LegalPage } from "@/pages/LegalPage";
import { TrustPage } from "@/pages/TrustPage";
import { validateReturnTo } from "@/lib/return-to";
import { RootLayout } from "@/layouts/RootLayout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { AdminGuard } from "@/lib/admin-guard";
import { DashboardPage } from "@/pages/DashboardPage";
import { VulnerabilitiesPage } from "@/pages/VulnerabilitiesPage";
import { VulnerabilityDetailPage } from "@/pages/VulnerabilityDetailPage";
import { ThreatActorsPage } from "@/pages/ThreatActorsPage";
import { ThreatActorDetailPage } from "@/pages/ThreatActorDetailPage";
import { campaignRoutes } from "@/pages/campaign-routes";
import { ThreatsLeakSitesPage } from "@/pages/ThreatsLeakSitesPage";
import { ThreatsSupplyChainPage } from "@/pages/ThreatsSupplyChainPage";
import { ProviderDetailPage } from "@/pages/ProviderDetailPage";
import { MapNextPage as MapPage } from "@/pages/MapNextPage";
import { BenchmarksPage } from "@/pages/BenchmarksPage";
import { RegulatoryPage } from "@/pages/RegulatoryPage";
import { RegulatoryFrameworkDetailPage } from "@/pages/RegulatoryFrameworkDetailPage";
import { RegulatoryCalendarPage } from "@/pages/RegulatoryCalendarPage";
import { RegulatoryComparePage } from "@/pages/RegulatoryComparePage";
import { SettingsPage } from "@/pages/SettingsPage";
import { StoriesListPage } from "@/pages/StoriesListPage";
import { StoryDetailPage } from "@/pages/StoryDetailPage";
import { AnalyticsPageShell } from "@/pages/AnalyticsPageShell";
import RegulatoryActionsPage from "@/pages/RegulatoryActionsPage";

import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage";
import { ContentModerationPage } from "@/pages/admin/ContentModerationPage";
import { SourcesPage } from "@/pages/admin/SourcesPage";
import { AIProvidersPage } from "@/pages/admin/AIProvidersPage";
import { AITasksPage } from "@/pages/admin/AITasksPage";
import { AICostDashboardPage } from "@/pages/admin/AICostDashboardPage";
import { EmailConfigPage } from "@/pages/admin/EmailConfigPage";
import { AdminVendorsListPage } from "@/pages/admin/AdminVendorsListPage";
import { AdminVendorNewPage } from "@/pages/admin/AdminVendorNewPage";
import { SystemHealthPage } from "@/pages/admin/SystemHealthPage";
import { ObservabilityPage } from "@/pages/admin/ObservabilityPage";
import { StoriesAdminPage } from "@/pages/admin/StoriesAdminPage";
import { StoryAdminDetailPage } from "@/pages/admin/StoryAdminDetailPage";
import { AuditLogPage } from "@/pages/admin/AuditLogPage";
import { LegalContentPage } from "@/pages/admin/LegalContentPage";
import { AnalyticsPage as AdminAnalyticsPage } from "@/pages/admin/AnalyticsPage";
import { BenchmarksAdminPage } from "@/pages/admin/BenchmarksAdminPage";
import { BenchmarkEditPage } from "@/pages/admin/BenchmarkEditPage";
import { BenchmarksPendingPage } from "@/pages/admin/BenchmarksPendingPage";
import { BenchmarkDetailPage } from "@/pages/BenchmarkDetailPage";
import { VulnerabilitiesAdminPage } from "@/pages/admin/VulnerabilitiesAdminPage";
import { VulnerabilityEditPage } from "@/pages/admin/VulnerabilityEditPage";
import { FrameworksAdminPage } from "@/pages/admin/FrameworksAdminPage";
import { AdminRegulatoryBriefsListPage } from "@/pages/admin/AdminRegulatoryBriefsListPage";
import { AdminRegulatoryBriefEditPage } from "@/pages/admin/AdminRegulatoryBriefEditPage";
import { AdminRegulatoryDeadlineEntryPage } from "@/pages/admin/AdminRegulatoryDeadlineEntryPage";
import { AdminRegulatoryDeadlinesListPage } from "@/pages/admin/AdminRegulatoryDeadlinesListPage";
import { AdminRegulatoryDigestRunsPage } from "@/pages/admin/AdminRegulatoryDigestRunsPage";
import AdminDbPerfPage from "@/pages/admin/AdminDbPerfPage";
import { ResearchReviewPage } from "@/pages/admin/ResearchReviewPage";
import { CpeMappingsPage } from "@/pages/admin/CpeMappingsPage";
import { RansomwareGroupsTriagePage } from "@/pages/admin/RansomwareGroupsTriagePage";
import { CountryOverridesPage } from "@/pages/admin/CountryOverridesPage";
import { isVulnsAdminEnabled } from "@/hooks/useAdminVulnerabilities";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { useSentryRouteTag } from "@/lib/sentry-route-tag";
import {
  useAnalyticsIdentity,
  usePageview,
  useReplayRecording,
} from "@/lib/analytics";
import { useBriefingAttribution } from "@/lib/briefing-attribution";

/**
 * Public `/login` route. Signed-out users see the sign-in surface. Signed-in
 * users are redirected (replace) to a validated safe `returnTo` from the query
 * string, or `/`. The login surface is also rendered inline by `ProtectedRoute`
 * for unauthenticated protected deep links — both paths share `LoginPage`.
 */
function PublicLoginRoute() {
  const { user, isLoading } = useAuth();
  const [params] = useSearchParams();

  if (isLoading) {
    return (
      <div
        className="flex h-screen items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <span
          className="h-6 w-6 animate-pulse rounded-full bg-muted"
          aria-hidden="true"
        />
        <span className="sr-only">Loading</span>
      </div>
    );
  }
  if (user) {
    return <Navigate to={validateReturnTo(params.get("returnTo"))} replace />;
  }
  return <LoginPage />;
}

/**
 * The full authenticated IntelHub surface: all user routes under `RootLayout`
 * and all admin routes under `AdminGuard`/`AdminLayout`. Mounted as a descendant
 * route group behind a single `ProtectedRoute` boundary so public routes can be
 * siblings outside it.
 */
function ProtectedAppRoutes() {
  return (
    <Routes>
      <Route element={<RootLayout />}>
        <Route index element={<DashboardPage />} />
        {/* Legacy /dashboard URL — Dashboard is the index route at `/`.
                Redirect so anyone typing /dashboard or following an old
                bookmark lands on the rendered dashboard instead of a blank
                page. (Sidebar nav uses `/`, but the URL bar didn't have an
                alias before P7 ship — hit during P7's prod walkthrough.) */}
        <Route path="dashboard" element={<Navigate to="/" replace />} />
        <Route path="vulnerabilities" element={<VulnerabilitiesPage />} />
        <Route
          path="vulnerabilities/:id"
          element={<VulnerabilityDetailPage />}
        />
        <Route path="threat-actors" element={<ThreatActorsPage />} />
        <Route path="threat-actors/:id" element={<ThreatActorDetailPage />} />
        {/* KAN-736 R24 (KAN-830 PR 10) — registered only while the campaign
            surface flag is on (codex r1 on PR 1393). */}
        {campaignRoutes()}
        <Route path="threats/leak-sites" element={<ThreatsLeakSitesPage />} />
        <Route
          path="threats/supply-chain"
          element={<ThreatsSupplyChainPage />}
        />
        <Route
          path="providers"
          element={
            <Navigate to="/threats/supply-chain?tab=providers" replace />
          }
        />
        <Route path="providers/:slug" element={<ProviderDetailPage />} />
        <Route path="map" element={<MapPage />} />
        <Route path="analytics" element={<AnalyticsPageShell />} />
        <Route path="benchmarks" element={<BenchmarksPage />} />
        <Route path="benchmarks/:id" element={<BenchmarkDetailPage />} />
        <Route path="regulatory" element={<RegulatoryPage />} />
        <Route
          path="regulatory/frameworks/:slug"
          element={<RegulatoryFrameworkDetailPage />}
        />
        <Route
          path="regulatory/calendar"
          element={<RegulatoryCalendarPage />}
        />
        <Route path="regulatory/compare" element={<RegulatoryComparePage />} />
        <Route path="regulatory-actions" element={<RegulatoryActionsPage />} />
        <Route path="stories" element={<StoriesListPage />} />
        <Route path="stories/:id" element={<StoryDetailPage />} />
        <Route path="articles" element={<Navigate to="/stories" replace />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route
        path="admin"
        element={
          <AdminGuard>
            <AdminLayout />
          </AdminGuard>
        }
      >
        <Route index element={<AdminDashboardPage />} />
        <Route path="content" element={<ContentModerationPage />} />
        <Route path="sources" element={<SourcesPage />} />
        <Route path="ai/providers" element={<AIProvidersPage />} />
        <Route path="ai/tasks" element={<AITasksPage />} />
        <Route path="ai/costs" element={<AICostDashboardPage />} />
        <Route path="email" element={<EmailConfigPage />} />
        <Route path="vendors" element={<AdminVendorsListPage />} />
        <Route path="vendors/new" element={<AdminVendorNewPage />} />
        <Route path="system" element={<SystemHealthPage />} />
        <Route path="observability" element={<ObservabilityPage />} />
        <Route path="stories" element={<StoriesAdminPage />} />
        <Route path="stories/:id" element={<StoryAdminDetailPage />} />
        <Route path="legal-content" element={<LegalContentPage />} />
        <Route path="audit" element={<AuditLogPage />} />
        <Route path="analytics" element={<AdminAnalyticsPage />} />
        <Route path="benchmarks" element={<BenchmarksAdminPage />} />
        <Route path="benchmarks/new" element={<BenchmarkEditPage />} />
        {/*
              SP2 pending-review queue. Placed BEFORE `:id/edit` so the
              literal "pending" segment doesn't match the `:id` param.
            */}
        <Route path="benchmarks/pending" element={<BenchmarksPendingPage />} />
        <Route path="benchmarks/:id/edit" element={<BenchmarkEditPage />} />
        {isVulnsAdminEnabled() && (
          <>
            <Route
              path="vulnerabilities"
              element={<VulnerabilitiesAdminPage />}
            />
            <Route
              path="vulnerabilities/:id/edit"
              element={<VulnerabilityEditPage />}
            />
          </>
        )}
        <Route path="frameworks" element={<FrameworksAdminPage />} />
        <Route
          path="regulatory-briefs"
          element={<AdminRegulatoryBriefsListPage />}
        />
        <Route
          path="regulatory-briefs/:id/edit"
          element={<AdminRegulatoryBriefEditPage />}
        />
        <Route
          path="regulatory-deadlines"
          element={<AdminRegulatoryDeadlinesListPage />}
        />
        <Route
          path="regulatory-deadlines/new"
          element={<AdminRegulatoryDeadlineEntryPage />}
        />
        <Route
          path="regulatory/digest-runs"
          element={<AdminRegulatoryDigestRunsPage />}
        />
        <Route path="db/perf" element={<AdminDbPerfPage />} />
        <Route path="cpe-mappings" element={<CpeMappingsPage />} />
        <Route
          path="ransomware-groups"
          element={<RansomwareGroupsTriagePage />}
        />
        <Route path="country-overrides" element={<CountryOverridesPage />} />
        <Route path="research-review" element={<ResearchReviewPage />} />
      </Route>
    </Routes>
  );
}

/**
 * KAN-32 analytics hooks that depend on the auth session.
 *
 * Deliberately NOT called in `App()` alongside `useSentryRouteTag()`: `App`
 * *renders* `<AuthProvider>`, so a hook in its body sits OUTSIDE the provider
 * and `useAuth()` would return the default context ({ user: null, isLoading:
 * true }) forever — identify would never fire and the failure would be silent.
 * Mounting as a child of `<AuthProvider>` is what puts the real session in
 * scope. Router context comes from `<BrowserRouter>` in main.tsx, above App.
 */
function AnalyticsBridge() {
  useAnalyticsIdentity();
  // KAN-482 F8 — `briefing:email_clicked`. Ordered AFTER useAnalyticsIdentity
  // so identify() has already bound distinct_id to the ZITADEL subject in this
  // same commit; a briefing click attributed to an anonymous id would be the
  // one event that cannot answer the question it exists for.
  useBriefingAttribution();
  usePageview();
  // A.10 (R10 / O8) — session replay, gated on all six preconditions and
  // re-evaluated on every route change. Lives here for the same reason as the
  // two above: preconditions 4, 5 and 6 all read the session. It starts
  // NOTHING unless an investigation is open AND VITE_POSTHOG_REPLAY_ENABLED is
  // 'true', which is set in no environment today.
  useReplayRecording();
  return null;
}

export default function App() {
  // Detect stale SW caches: compares build hash against server on load + interval
  useVersionCheck();
  // Tag every Sentry event with the normalised route pattern
  useSentryRouteTag();

  return (
    <AuthProvider>
      <AnalyticsBridge />
      <Routes>
        {/* Public siblings — reachable before sign-in, outside ProtectedRoute. */}
        <Route path="/login" element={<PublicLoginRoute />} />
        {/* Invite redeem: signed-out invitees land here from the emailed link.
            Must be public (outside ProtectedRoute) so it isn't bounced to
            login before the token is stashed. NOT under /api, so workbox's
            navigateFallbackDenylist serves the SPA shell normally. */}
        <Route path="/invite" element={<InviteRedeemPage />} />
        <Route
          path="/privacy"
          element={<LegalPage kind="privacy" />}
        />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/trust" element={<TrustPage />} />
        {/* Everything else is the protected app/admin surface. */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <ProtectedAppRoutes />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
}
